/**
 * Builds the base path for the REST API using the configured subdirectory.
 * @returns {string} The API base path (e.g. '/marine/api').
 */
const getApiBase = () => {
  const subdirectory = window?.AppData?.subdirectory ?? 'marine';
  return `/${subdirectory}/api`;
};

/**
 * Fetches map cards for a given location from the REST API.
 * @param {object} params - The request parameters.
 * @param {string} params.lat - Latitude of the search location.
 * @param {string} params.long - Longitude of the search location.
 * @param {string} params.locale - Locale in xx-XX format (e.g. 'en-US').
 * @param {boolean} params.isGarminMap - True for Garmin maps, false for Navionics.
 * @returns {Promise<Array<object>>} Resolves with the list of map cards.
 * @throws {Error} When the response status is not OK.
 */
export const fetchMapCards = async ({
  lat, long, locale, isGarminMap,
}) => {
  const base = getApiBase();
  const params = new URLSearchParams({
    lat, long, locale, isGarminMap,
  });
  const response = await fetch(`${base}/mapCards/?${params}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`fetchMapCards failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetches lakes near a given location from the REST API.
 * @param {object} params - The request parameters.
 * @param {string} params.lat - Latitude of the search location.
 * @param {string} params.long - Longitude of the search location.
 * @param {boolean} params.isGarminMap - True for Garmin maps, false for Navionics.
 * @returns {Promise<Array<object>>} Resolves with the list of lakes.
 * @throws {Error} When the response status is not OK.
 */
export const fetchLakesByLatLong = async ({ lat, long, isGarminMap }) => {
  const base = getApiBase();
  const params = new URLSearchParams({ lat, long, isGarminMap });
  const response = await fetch(`${base}/lakes/?${params}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`fetchLakesByLatLong failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Searches for lakes by name via the REST API.
 * @param {object} params - The request parameters.
 * @param {string} params.name - The lake name to search for.
 * @param {boolean} params.isGarminMap - True for Garmin maps, false for Navionics.
 * @returns {Promise<Array<object>>} Resolves with the matching lakes.
 * @throws {Error} When the response status is not OK.
 */
export const fetchLakesByName = async ({ name, isGarminMap }) => {
  const base = getApiBase();
  const params = new URLSearchParams({ name, isGarminMap });
  const response = await fetch(`${base}/lakes/search/?${params}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`fetchLakesByName failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetches promoted (sale) pricing for a list of skus via the REST API.
 * @param {object} params - The request parameters.
 * @param {Array<string>} params.skus - The product skus to price (max 100).
 * @param {string} params.locale - Locale in xx-XX format (e.g. 'en-US').
 * @returns {Promise<Array<object>|null>} Resolves with promoted prices, or null when disabled.
 * @throws {Error} When the response status is not OK.
 */
export const fetchPromotedPricing = async ({ skus, locale }) => {
  const base = getApiBase();
  const response = await fetch(`${base}/promotedPricing/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skus, locale }),
  });
  if (!response.ok) {
    throw new Error(`fetchPromotedPricing failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export default {
  fetchMapCards,
  fetchLakesByLatLong,
  fetchLakesByName,
  fetchPromotedPricing,
};
